/**
 * The record screen's waveform. Bars grow up and down from a centre line.
 *
 * idle      a quiet grey outline, so the panel never looks empty
 * recording the live microphone level, newest bar on the right
 * paused    the live bars, frozen and dimmed
 * ready     the whole take; bars fill in as the playback runs
 */
export type VoiceWaveMode = "idle" | "recording" | "paused" | "ready";

const IDLE_SHAPE = (count: number) =>
  Array.from({ length: count }, (_, index) => {
    const t = index / Math.max(1, count - 1);
    const envelope = Math.sin(Math.PI * t) * 0.75 + 0.25;
    const ripple = 0.55 + 0.45 * Math.abs(Math.sin(index * 1.7) * Math.cos(index * 0.6));
    return Math.max(0.12, envelope * ripple * 0.7);
  });

export function VoiceWave({
  levels,
  mode,
  progress = 0,
}: {
  levels: number[];
  mode: VoiceWaveMode;
  /** 0..1 of the playback, for the ready state. */
  progress?: number;
}) {
  const bars = mode === "idle" ? IDLE_SHAPE(levels.length) : levels;
  const played = Math.round(progress * bars.length);

  return (
    <div className={`voice-wave is-${mode}`} aria-hidden="true">
      {bars.map((level, index) => (
        <span
          key={index}
          className={mode === "ready" && index < played ? "is-played" : undefined}
          style={{ height: `${Math.max(6, Math.round(level * 100))}%` }}
        />
      ))}
    </div>
  );
}
